import { persistentKeyValueStore } from '~/common/services/persistentKeyValueStore';
import { AuthSession } from '../../../types';
import { StorageKey } from '../../inceptPersistentStorage';
import { deriveSessionFromStored, unauthenticatedSession } from '../session';

export type AuthSessionStore = {
  subscribe: (callback: () => void) => () => void;
  getSnapshot: () => AuthSession;
  getServerSnapshot: () => AuthSession;
  setSession: (session: AuthSession) => void;
};

// The flat token shape written to storage. Kept byte-compatible with the legacy
// pre-session shape so a tab still running old code reads it unchanged during a
// deploy, and so deriveSessionFromStored can rebuild the status from it.
type StoredTokens = {
  jwt: string;
  expiresAt?: string;
  twoFactorToken?: string;
};

const toStoredTokens = (session: AuthSession): StoredTokens | undefined => {
  if (session.status === 'unauthenticated' || !session.jwt) {
    return undefined;
  }

  return {
    jwt: session.jwt,
    expiresAt: session.expiresAt,
    ...(session.twoFactorToken
      ? { twoFactorToken: session.twoFactorToken }
      : {}),
  };
};

// SSR-safe default. A single shared reference so getServerSnapshot is stable.
const SERVER_SNAPSHOT = unauthenticatedSession();

const createAuthSessionStore = (namespace: string): AuthSessionStore => {
  const store = persistentKeyValueStore(namespace);
  const namespacedKeySuffix = `||${StorageKey.Tokens}`;
  // Per-namespace same-tab event: `storage` events never fire in the writing
  // tab, so setSession dispatches this to notify this tab's subscribers.
  const sameTabEventName = `incept-auth-session-change::${namespace}`;

  const readFromStorage = (): AuthSession =>
    deriveSessionFromStored(store.getValueForKey(StorageKey.Tokens));

  // Cached reference returned by getSnapshot. Never re-derived on read (that
  // would hand useSyncExternalStore a new identity every call and loop); only a
  // write or an external storage change replaces it.
  let cachedSession = readFromStorage();

  const getSnapshot = (): AuthSession => cachedSession;

  const getServerSnapshot = (): AuthSession => SERVER_SNAPSHOT;

  const subscribe = (callback: () => void): (() => void) => {
    // Cross-tab: another tab (or a storage clear) changed the persisted value.
    // Re-read storage into the cache before notifying.
    const handleStorage = (event: StorageEvent): void => {
      // event.key is null when storage was cleared entirely.
      if (event.key === null || event.key.endsWith(namespacedKeySuffix)) {
        cachedSession = readFromStorage();
        callback();
      }
    };

    // Same-tab: setSession already updated the cache, so just notify.
    const handleSameTab = (): void => {
      callback();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(sameTabEventName, handleSameTab);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(sameTabEventName, handleSameTab);
    };
  };

  const setSession = (session: AuthSession): void => {
    // Update the in-memory cache first so this tab stays consistent even if the
    // storage write throws (quota/availability).
    cachedSession = session;

    try {
      const stored = toStoredTokens(session);
      if (stored) {
        store.setValueForKey(StorageKey.Tokens, stored);
      } else {
        store.deleteValueForKey(StorageKey.Tokens);
      }
    } catch {
      // In-memory cache is already updated; the tab stays usable.
    }

    window.dispatchEvent(new Event(sameTabEventName));
  };

  return { subscribe, getSnapshot, getServerSnapshot, setSession };
};

// Memoised per namespace: every consumer of a portal's session shares one store
// instance (and therefore one cache), so a same-tab write is seen everywhere.
const storesByNamespace = new Map<string, AuthSessionStore>();

export const getAuthSessionStore = (namespace: string): AuthSessionStore => {
  const existing = storesByNamespace.get(namespace);
  if (existing) {
    return existing;
  }

  const created = createAuthSessionStore(namespace);
  storesByNamespace.set(namespace, created);
  return created;
};
